import React from 'react'
import { Session, SessionFilters, ConciergeMode } from '../types/communication.types'
import { SessionCard } from './SessionCard'
import { SessionFiltersComponent } from './SessionFilters'
import { DebugPanel } from './DebugPanel'

interface SessionsListProps {
  sessions: Session[]
  activeSession: Session | null
  isLoading: boolean
  error: string | null
  filters: SessionFilters
  onSessionSelect: (session: Session) => void
  onFiltersChange: (filters: Partial<SessionFilters>) => void
  onRefresh: () => void
  onConciergeModeChange: (sessionKey: string, mode: ConciergeMode) => Promise<void>
  aiSuggestions?: Array<{
    id: string
    sessionKey: string
    suggestion: string
    sentiment: string
    originalPrompt: string
    timestamp: number
  }>
  debugInfo?: {
    rawSessionsCount: number
    groupedSessionsCount: number
    duplicatesInfo: Array<{
      guestName: string
      sessionKey: string
      duplicateCount: number
      totalUnread: number
    }>
  }
}

export const SessionsList: React.FC<SessionsListProps> = ({
  sessions,
  activeSession,
  isLoading,
  error,
  filters,
  onSessionSelect,
  onFiltersChange,
  onRefresh,
  onConciergeModeChange,
  aiSuggestions = [],
  debugInfo
}) => {
  const unreadCount = sessions.filter(s => s.unreadCount && s.unreadCount > 0).length
  const connectedCount = sessions.filter(s => s.connected).length

  return (
    <div className="d-flex flex-column h-100">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h3 className="fw-bold text-gray-800 mb-0">
          Sessões Ativas
          {sessions.length > 0 && (
            <span className="ms-2 badge badge-light-primary">
              {sessions.length}
            </span>
          )}
        </h3>
        
        <button
          className="btn btn-sm btn-light-primary"
          onClick={onRefresh}
          disabled={isLoading}
          title="Atualizar sessões"
        >
          <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
        </button>
      </div>

      {/* Debug Panel - Apenas se houver duplicatas */}
      {debugInfo && (
        <DebugPanel
          rawSessionsCount={debugInfo.rawSessionsCount}
          groupedSessionsCount={debugInfo.groupedSessionsCount}
          duplicatesInfo={debugInfo.duplicatesInfo}
        />
      )}

      {/* Filtros */}
      <SessionFiltersComponent
        filters={filters}
        onFiltersChange={onFiltersChange}
        totalSessions={sessions.length}
        unreadCount={unreadCount}
        connectedCount={connectedCount}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="text-muted mt-3">Carregando sessões...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Erro:</strong> {error}
            <button
              className="btn btn-sm btn-danger ms-2"
              onClick={onRefresh}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}

      {/* Lista de Sessões */}
      <div className="flex-grow-1 overflow-auto">
        {!isLoading && !error && (
          <>
            {sessions.length === 0 ? (
              <EmptyState filters={filters} />
            ) : (
              <div className="sessions-list">
                {sessions.map(session => (
                  <SessionCard
                    key={session.sessionKey}
                    session={session}
                    isActive={activeSession?.sessionKey === session.sessionKey}
                    onClick={() => onSessionSelect(session)}
                    onConciergeModeChange={onConciergeModeChange}
                    debugInfo={(session as any)._debugInfo}
                    aiSuggestionsCount={aiSuggestions.filter(s => s.sessionKey === session.sessionKey).length}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer com estatísticas */}
      {!isLoading && !error && sessions.length > 0 && (
        <div className="mt-4 p-3 bg-light rounded">
          <div className="row text-center">
            <div className="col-4">
              <div className="fw-bold text-primary">{sessions.length}</div>
              <div className="fs-7 text-muted">Total</div>
            </div>
            <div className="col-4">
              <div className="fw-bold text-success">{connectedCount}</div>
              <div className="fs-7 text-muted">Online</div>
            </div>
            <div className="col-4">
              <div className="fw-bold text-danger">{unreadCount}</div>
              <div className="fs-7 text-muted">Não lidas</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Componente para estado vazio
const EmptyState: React.FC<{ filters: SessionFilters }> = ({ filters }) => {
  const hasFilters = 
    filters.showUnreadOnly || 
    filters.showConnectedOnly || 
    filters.departments.length > 0 || 
    filters.searchQuery.trim().length > 0

  return (
    <div className="text-center py-8">
      <div className="mb-4">
        <i className="fas fa-comments fs-3x text-muted"></i>
      </div>
      
      <h5 className="text-gray-800 mb-2">
        {hasFilters ? 'Nenhuma sessão encontrada' : 'Nenhuma conversa ativa no momento'}
      </h5>
      
      <p className="text-muted">
        {hasFilters
          ? 'Tente ajustar os filtros para ver mais resultados.'
          : 'As conversas dos hóspedes aparecerão aqui assim que eles iniciarem contato através do chat do hotel. Se esta é a primeira vez que está usando a central de comunicação, as sessões começarão a aparecer quando os hóspedes enviarem suas primeiras mensagens.'
        }
      </p>

      {hasFilters && (
        <button 
          className="btn btn-sm btn-light-primary mt-3"
          onClick={() => {
            // Esta função seria passada do componente pai
            console.log('Limpar filtros')
          }}
        >
          <i className="fas fa-times me-1"></i>
          Limpar Filtros
        </button>
      )}

      {!hasFilters && (
        <div className="mt-4 p-3 bg-light rounded">
          <small className="text-muted">
            <i className="fas fa-info-circle me-1"></i>
            <strong>Dica:</strong> Para testar o sistema, você pode simular uma conversa usando as ferramentas de diagnóstico disponíveis no topo da página.
          </small>
        </div>
      )}
    </div>
  )
}